/**
 * The public catalog's language and genre filters (`GET /api/novels?language=
 * &genre=`, the web's `/catalog?lang=&genre=`), normalized the same way by the
 * API, the web and the database's `list_novel_page` (migration 0041):
 *
 * - text is compared with its ASCII whitespace (space, tab, newline, CR, FF,
 *   VT) collapsed to one space and trimmed, lowercase — ASCII only, the SQL's
 *   `[ \t\n\r\f\v]` (Postgres' `\s` and JS's `trim()` disagree on NBSP & co.) —
 *   with the two lowercase results JS and Postgres disagree on folded to
 *   Postgres' (see normalizeCatalogText);
 * - a language is the primary subtag of a code ("RU", "pt-BR" → "pt"); a novel
 *   matches by the primary subtag of its own `language`;
 * - a genre tag belongs to a catalog GENRE: a known genre's id when its
 *   spelling is one of that genre's names (in any UI language, or a common
 *   synonym: "Хоррор", "Horreur", "Terror" → "horror"), else the spelling
 *   itself. A novel matches when any of its tags belongs to the chosen genre.
 *
 * Genre tags are written in the novel's own language (expandPrompt), so one
 * genre arrives under many spellings; grouping them here is what lets one
 * "Horror" chip find an English "Horror" and a Russian «Ужасы» alike. The SQL
 * does not hold the table: the API sends it the genre's spellings
 * (catalogGenreSpellings) and the database compares normalized tags to them.
 */

/** Longest genre a filter may name (its normalized spelling); longer is refused. */
export const MAX_CATALOG_GENRE_LENGTH = 40;

const LANGUAGE_PATTERN = /^[a-z]{2,3}$/;
const ASCII_WHITESPACE_RUN = /[ \t\n\r\f\v]+/g;
const FINAL_SIGMA = /ς/g;
const DOTTED_I = /i̇/g;
// Control characters never belong in a genre tag (or a cache key built from one).
const CONTROL_CHARACTERS = /\p{Cc}/u;

/**
 * Genres the catalog groups under one id, each with every spelling a tag may
 * carry for it: the English name, common synonyms, and its label in every UI
 * dictionary (apps/web `catalogGenres.test.ts` fails when a dictionary label is
 * missing here). "Detective" is filed under Mystery and "Suspense" under
 * Thriller: several dictionaries give each pair one word (ru «Детектив», pt
 * «Suspense»), and two chips with the same text would each find only half.
 * `name` is the English name the web localizes (its `localizeGenre`).
 */
export const KNOWN_CATALOG_GENRES: ReadonlyArray<{ id: string; name: string; spellings: readonly string[] }> = [
  {
    id: "mystery",
    name: "Mystery",
    spellings: [
      "mystery", "mysteries", "detective", "detective fiction", "whodunit",
      "mystère", "enquête", "misterio", "detectives", "mistério", "detetive", "detektiv",
      "детектив", "ミステリー", "探偵", "悬疑", "侦探"
    ]
  },
  { id: "romance", name: "Romance", spellings: ["romance", "romantic", "romantik", "романтика", "любовный роман", "ロマンス", "爱情"] },
  { id: "fantasy", name: "Fantasy", spellings: ["fantasy", "fantastique", "fantasía", "fantasia", "фэнтези", "ファンタジー", "奇幻"] },
  {
    id: "sci-fi",
    name: "Sci-Fi",
    spellings: [
      "sci-fi", "scifi", "sci fi", "science fiction", "science-fiction", "sf",
      "ciencia ficción", "ficção científica", "научная фантастика", "фантастика", "科幻"
    ]
  },
  { id: "horror", name: "Horror", spellings: ["horror", "horreur", "terror", "хоррор", "ужасы", "ホラー", "恐怖"] },
  {
    id: "thriller",
    name: "Thriller",
    spellings: ["thriller", "suspense", "spannung", "триллер", "саспенс", "スリラー", "サスペンス", "惊悚", "悬念"]
  },
  { id: "adventure", name: "Adventure", spellings: ["adventure", "abenteuer", "aventure", "aventura", "приключения", "アドベンチャー", "冒险"] },
  { id: "drama", name: "Drama", spellings: ["drama", "drame", "драма", "ドラマ", "剧情"] },
  { id: "comedy", name: "Comedy", spellings: ["comedy", "komödie", "comédie", "comedia", "comédia", "комедия", "コメディ", "喜剧"] },
  {
    id: "historical",
    name: "Historical",
    spellings: ["historical", "historisch", "historique", "histórico", "историческое", "исторический", "歴史", "历史"]
  },
  {
    id: "supernatural",
    name: "Supernatural",
    spellings: ["supernatural", "übernatürlich", "surnaturel", "sobrenatural", "мистика", "超常", "超自然"]
  },
  {
    id: "psychological",
    name: "Psychological",
    spellings: ["psychological", "psychologisch", "psychologique", "psicológico", "психологическое", "психологический", "心理"]
  },
  { id: "gothic", name: "Gothic", spellings: ["gothic", "gothique", "gótico", "готика", "ゴシック", "哥特"] },
  { id: "school", name: "School", spellings: ["school", "schule", "école", "escolar", "escola", "школа", "学園", "校园"] },
  { id: "noir", name: "Noir", spellings: ["noir", "нуар", "ノワール", "黑色"] },
  { id: "crime", name: "Crime", spellings: ["crime", "krimi", "crimen", "криминал", "犯罪"] },
  { id: "paranormal", name: "Paranormal", spellings: ["paranormal", "паранормальное", "パラノーマル", "灵异"] },
  { id: "action", name: "Action", spellings: ["action", "acción", "ação", "экшен", "боевик", "アクション", "动作"] },
  {
    id: "tragedy",
    name: "Tragedy",
    spellings: ["tragedy", "tragödie", "tragédie", "tragedia", "tragédia", "трагедия", "悲劇", "悲剧"]
  },
  {
    id: "dystopian",
    name: "Dystopian",
    spellings: ["dystopian", "dystopia", "dystopie", "distopía", "distopia", "антиутопия", "ディストピア", "反乌托邦"]
  },
  { id: "cyberpunk", name: "Cyberpunk", spellings: ["cyberpunk", "киберпанк", "サイバーパンク", "赛博朋克"] },
  {
    id: "post-apocalyptic",
    name: "Post-apocalyptic",
    spellings: [
      "post-apocalyptic", "post apocalyptic", "postapocalyptic", "postapokalypse", "post-apocalyptique",
      "postapocalíptico", "pós-apocalíptico", "постапокалипсис", "ポストアポカリプス", "末日后"
    ]
  },
  {
    id: "slice-of-life",
    name: "Slice of life",
    spellings: ["slice of life", "alltag", "tranche de vie", "vida cotidiana", "cotidiano", "повседневность", "日常"]
  },
  {
    id: "survival",
    name: "Survival",
    spellings: ["survival", "survie", "supervivencia", "sobrevivência", "выживание", "サバイバル", "生存"]
  },
  {
    id: "dark-fantasy",
    name: "Dark fantasy",
    spellings: [
      "dark fantasy", "fantasía oscura", "fantasia sombria", "тёмное фэнтези", "темное фэнтези",
      "ダークファンタジー", "黑暗奇幻"
    ]
  },
  {
    id: "urban-fantasy",
    name: "Urban fantasy",
    spellings: [
      "urban fantasy", "fantasy urbaine", "fantasía urbana", "fantasia urbana", "городское фэнтези",
      "現代ファンタジー", "都市奇幻"
    ]
  },
  {
    id: "occult",
    name: "Occult",
    spellings: ["occult", "okkultismus", "occultisme", "ocultismo", "оккультизм", "オカルト", "神秘学"]
  },
  { id: "folklore", name: "Folklore", spellings: ["folklore", "folclore", "фольклор", "民話", "民间传说"] }
];

/**
 * Text as the catalog compares it: ASCII whitespace collapsed to one space and
 * trimmed, lowercase. JS and Postgres lowercase two things differently — a
 * capital sigma at the end of a word (JS: final "ς", Postgres: "σ") and the
 * Turkish capital "İ" (JS: "i" + combining dot U+0307, Postgres: plain "i") —
 * so both are folded to Postgres' form, exactly as migration 0041 folds the
 * stored tags; otherwise a chip the facets offer ("ΤΡΟΜΟΣ") would find nothing.
 */
export function normalizeCatalogText(text: string): string {
  const collapsed = text.replace(ASCII_WHITESPACE_RUN, " ");
  const start = collapsed.startsWith(" ") ? 1 : 0;
  const end = Math.max(start, collapsed.endsWith(" ") ? collapsed.length - 1 : collapsed.length);
  return collapsed.slice(start, end).toLowerCase().replace(FINAL_SIGMA, "σ").replace(DOTTED_I, "i");
}

const GENRE_ID_BY_SPELLING = new Map<string, string>();
const GENRE_BY_ID = new Map<string, { name: string; spellings: string[] }>();
for (const genre of KNOWN_CATALOG_GENRES) {
  // A genre's own id is one of its spellings, so a key always maps back to itself.
  const spellings = [...new Set([genre.id, ...genre.spellings].map(normalizeCatalogText))];
  for (const spelling of spellings) GENRE_ID_BY_SPELLING.set(spelling, genre.id);
  GENRE_BY_ID.set(genre.id, { name: genre.name, spellings });
}

/** The catalog genre a tag belongs to: a known genre's id, else the tag's normalized spelling. */
export function catalogGenreKey(tag: string): string {
  const spelling = normalizeCatalogText(tag);
  return GENRE_ID_BY_SPELLING.get(spelling) ?? spelling;
}

/** Whether a genre key names a known genre (KNOWN_CATALOG_GENRES), not free text a tag carried. */
export function isKnownCatalogGenre(key: string): boolean {
  return GENRE_BY_ID.has(key);
}

/** A genre key's display name: a known genre's English name ("sci-fi" → "Sci-Fi"), else the key itself. */
export function catalogGenreName(key: string): string {
  return GENRE_BY_ID.get(key)?.name ?? key;
}

/** Every normalized spelling that belongs to a genre key — what the database compares tags to. */
export function catalogGenreSpellings(key: string): string[] {
  return [...(GENRE_BY_ID.get(key)?.spellings ?? [key])];
}

/** A language filter value as its lowercase primary subtag, or null when it is not a language code. */
export function normalizeCatalogLanguage(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const primary = normalizeCatalogText(raw).split(/[-_]/)[0] ?? "";
  return LANGUAGE_PATTERN.test(primary) ? primary : null;
}

/**
 * Why a genre filter value cannot be one: "empty" (only whitespace, or not a
 * string), "too_long" (a spelling over MAX_CATALOG_GENRE_LENGTH) or
 * "control_characters"; null when it is fine.
 */
export function catalogGenreProblem(raw: unknown): "empty" | "too_long" | "control_characters" | null {
  if (typeof raw !== "string") return "empty";
  const spelling = normalizeCatalogText(raw);
  if (!spelling) return "empty";
  if (spelling.length > MAX_CATALOG_GENRE_LENGTH) return "too_long";
  if (CONTROL_CHARACTERS.test(spelling)) return "control_characters";
  return null;
}

/** A genre filter value as its genre key (catalogGenreKey), or null when it cannot be one (catalogGenreProblem). */
export function normalizeCatalogGenre(raw: unknown): string | null {
  if (typeof raw !== "string" || catalogGenreProblem(raw) !== null) return null;
  return catalogGenreKey(raw);
}

/** Normalized catalog filters; an absent field does not filter. */
export interface CatalogFilterValues {
  language?: string | null;
  genre?: string | null;
}

/**
 * Whether a novel (or its catalog summary) passes the filters. `filters` must
 * already be normalized (normalizeCatalogLanguage / normalizeCatalogGenre).
 */
export function matchesCatalogFilters(
  novel: { language?: string | null; genre?: readonly string[] | null },
  filters: CatalogFilterValues
): boolean {
  if (filters.language && normalizeCatalogLanguage(novel.language) !== filters.language) return false;
  if (filters.genre) {
    const wanted = filters.genre;
    if (!(novel.genre ?? []).some((tag) => typeof tag === "string" && catalogGenreKey(tag) === wanted)) return false;
  }
  return true;
}
